import { Injectable } from '@angular/core';
import { ApiKeysResource, ApiKeyResource } from './account-response.types';
import { switchMap, tap } from 'rxjs/operators';
import { ApiService, getEmbeddedCollection, getLink } from '@mode/shared/util-halogen';
import { OrgTypes } from '@mode/shared/types-common';
import { from, Observable, throwError } from 'rxjs';
import { AccountFetchService } from './account-fetch.service';
import { apiKeyCache, apiKeysCache } from '../resource-cache';
import { ApiTypes, ModeForm } from '@mode/shared/types-common';

@Injectable({
  providedIn: 'root',
})
export class ApiKeyFetchService {
  constructor(private accountFetcher: AccountFetchService, private apiService: ApiService) {}

  public getWorkspaceApiKeys(username: string, page: number, query: string) {
    return this.accountFetcher.getAccountResource(username).pipe(
      switchMap((account) => {
        const apiKeysLink = getLink(account, 'api_keys') as ApiTypes.HalLink;
        const apiKeysOptions = {
          params: { username, page, query },
          includes: {
            api_keys: ['creator', 'revoker'],
          },
        };
        return from(this.apiService.getFromPath<ApiKeysResource>(apiKeysLink?.href, apiKeysOptions));
      }),
      tap((response: ApiKeysResource) => {
        apiKeysCache.set(username, response);
        const apiKeys = getEmbeddedCollection<ApiKeyResource>(response, 'api_keys') || [];
        apiKeys.map((apiKey: ApiKeyResource) => apiKeyCache.set(apiKey['token_name'], apiKey));
      })
    );
  }

  public revokeApiKey(token: string) {
    const resource = apiKeyCache.get(token);

    if (resource != null) {
      return from(this.apiService.submitForm<ApiKeyResource>(resource, 'revoke', {}));
    } else {
      return throwError(new Error(`ApiKey ${token} is not in the cache`));
    }
  }

  public revokeAllApiKeys(workspace: string) {
    const resource = apiKeysCache.get(workspace) as ApiKeysResource;
    const revokeAllLink = getLink(resource, 'revoke_all') as ApiTypes.HalLink;
    const revokeAllForm = { action: revokeAllLink?.href, method: 'patch' } as ModeForm;
    const revokeAllParams = {};

    if (resource != null) {
      return from(this.apiService.submit<ApiKeysResource>(revokeAllForm, revokeAllParams));
    } else {
      return throwError(new Error(`ApiKeys for workspace "${workspace}" is out of sync with cache`));
    }
  }

  public createApiKey(username: string, apiKeyData: OrgTypes.ApiKeyCreate): Observable<ApiKeyResource> {
    const resource = apiKeysCache.get(username);

    const apiKey = {
      name: apiKeyData.name,
      expires_at: apiKeyData.expiresAt,
    };

    const params = {
      workspace_api_key: apiKey,
    };

    if (resource != null) {
      return from(this.apiService.submitForm<ApiKeyResource>(resource, 'create', params));
    } else {
      return throwError(new Error(`ApiKeys for ${username} is not in the cache`));
    }
  }
}
